import { useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, UserPlus, Copy, ChevronLeft, ChevronRight, Eye, Pencil, Trash2, MoreHorizontal, Mail } from "lucide-react";
import { ChangeStudentEmailDialog } from "@/components/user-management/ChangeStudentEmailDialog";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InviteUserForm } from "@/components/forms/InviteUserForm";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { AllUsersTab } from "@/components/user-management/AllUsersTab";
import { ModuleVisibilityMatrix } from "@/components/user-management/ModuleVisibilityMatrix";
import { StudentProfileCard } from "@/components/student/StudentProfileCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { StudentForm } from "@/components/forms/StudentForm";
import { StudentSimulationCard } from "@/components/user-management/StudentSimulationCard";
import { SignupHealthCard } from "@/components/user-management/SignupHealthCard";
import { AssignCrRoleTab } from "@/components/user-management/AssignCrRoleTab";


const roleLabels: Record<string, string> = {
  superadmin: "Super Admin",
  principal: "Principal",
  deputy_principal: "Deputy Principal",
  head_admission: "Head of Admission",
  head_administration: "Head of Administration",
  head_academic: "Head of Academic",
  head_examination: "Head of Examination",
  head_accounting: "Head of Accounting",
  head_student_service: "Head of Student Services",
  head_discipline: "Head of Discipline",
  hod_cmt: "HoD CMT",
  hod_pst: "HoD PST",
  hod_sw: "HoD SW",
  it_incharge: "IT Incharge",
  security_man: "Security Man",
  cr_cmt4: "CR CMT4",
  cr_cmt5: "CR CMT5",
  cr_cmt6: "CR CMT6",
  cr_pst4: "CR PST4",
  cr_pst5: "CR PST5",
  cr_pst6: "CR PST6",
  cr_sw4: "CR SW4",
  cr_sw5: "CR SW5",
  cr_sw6: "CR SW6",
  auditor: "Auditor",
  student: "Student",
};

const STUDENTS_PER_PAGE = 10;

const UserManagement = () => {
  const { toast } = useToast();
  const { user, roles } = useAuth();
  const isSuperadmin = roles.includes("superadmin");
  const isHeadAdmin = roles.includes("head_administration");
  const canChangeEmail = isSuperadmin || isHeadAdmin;
  const queryClient = useQueryClient();
  const [studentSearch, setStudentSearch] = useState("");
  const [studentPage, setStudentPage] = useState(1);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [viewStudent, setViewStudent] = useState<any>(null);
  const [editStudent, setEditStudent] = useState<any>(null);
  const [deleteStudent, setDeleteStudent] = useState<any>(null);
  const [emailStudent, setEmailStudent] = useState<any>(null);

  // Students query — now includes full student details
  const { data: studentUsers = [], isLoading: loadingStudents } = useQuery({
    queryKey: ["student-users-full"],
    queryFn: async () => {
      const { data: profiles, error } = await supabase.from("profiles").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      const { data: roles } = await supabase.from("user_roles").select("*");
      const { data: students } = await supabase.from("students").select("*");
      const studentMap = new Map((students ?? []).map(s => [s.user_id, s]));
      return (profiles ?? [])
        .map(p => {
          const userRoles = (roles ?? []).filter(r => r.user_id === p.user_id).map(r => r.role);
          const studentRecord = studentMap.get(p.user_id);
          return {
            ...p,
            roles: userRoles,
            student_number: studentRecord?.student_number || null,
            studentRecord: studentRecord || null,
          };
        })
        .filter(u => u.roles.includes("student"))
        .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));
    },
  });

  const { data: invitations = [], isLoading: loadingInvitations } = useQuery({
    queryKey: ["invitations"],
    queryFn: async () => {
      const { data, error } = await supabase.from("invitations").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const deleteStudentMutation = useMutation({
    mutationFn: async (userId: string) => {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/delete-user`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ user_id: userId }),
        }
      );
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Failed to delete user");
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student-users-full"] });
      toast({ title: "Student deleted permanently" });
      setDeleteStudent(null);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Student filtering + pagination
  const filteredStudents = studentUsers.filter(u =>
    `${u.full_name} ${u.student_number || ""} ${u.studentRecord?.class || ""}`.toLowerCase().includes(studentSearch.toLowerCase())
  );
  const totalStudentPages = Math.max(1, Math.ceil(filteredStudents.length / STUDENTS_PER_PAGE));
  const currentPage = Math.min(studentPage, totalStudentPages);
  const paginatedStudents = filteredStudents.slice(
    (currentPage - 1) * STUDENTS_PER_PAGE,
    currentPage * STUDENTS_PER_PAGE
  );

  const copyInviteLink = (token: string) => {
    const appUrl = import.meta.env.VITE_APP_URL || "https://stdavidkicollege.mindsetsolutionsafrica.group";
    const url = `${appUrl}/accept-invitation?token=${token}`;
    navigator.clipboard.writeText(url);
    toast({ title: "Link copied!", description: "Share this link with the invited user." });
  };

  return (
    <AppLayout>
      <div className="space-y-3 sm:space-y-4">
        <div className="page-header">
          <div>
            <h1>User Management</h1>
            <p className="text-muted-foreground text-xs sm:text-sm">Manage users, roles, module visibility, and permissions</p>
          </div>
          <Button size="sm" className="sm:size-default" onClick={() => setInviteOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />Pre-Register Staff
          </Button>
        </div>

        <InviteUserForm open={inviteOpen} onOpenChange={setInviteOpen} onSuccess={() => queryClient.invalidateQueries({ queryKey: ["invitations"] })} />

        {/* View Student Dialog */}
        <Dialog open={!!viewStudent} onOpenChange={(open) => !open && setViewStudent(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-sm">Student Details</DialogTitle>
            </DialogHeader>
            {viewStudent?.studentRecord && (
              <StudentProfileCard student={viewStudent.studentRecord} />
            )}
            {!viewStudent?.studentRecord && (
              <p className="text-sm text-muted-foreground text-center py-4">No detailed student record found.</p>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Student Dialog */}
        {editStudent?.studentRecord && (
          <StudentForm
            open={!!editStudent}
            onOpenChange={(open) => !open && setEditStudent(null)}
            student={editStudent.studentRecord}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ["student-users-full"] });
              setEditStudent(null);
              toast({ title: "Student updated" });
            }}
          />
        )}

        {/* Delete Student Dialog */}
        <AlertDialog open={!!deleteStudent} onOpenChange={(open) => !open && setDeleteStudent(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Student</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{deleteStudent?.full_name}</strong>'s account and all related records. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleteStudentMutation.isPending}
                onClick={() => deleteStudent && deleteStudentMutation.mutate(deleteStudent.user_id)}
              >
                {deleteStudentMutation.isPending ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <ChangeStudentEmailDialog
          open={!!emailStudent}
          onOpenChange={(open) => !open && setEmailStudent(null)}
          userId={emailStudent?.user_id ?? null}
          studentName={emailStudent?.full_name ?? null}
          currentEmail={emailStudent?.studentRecord?.email ?? null}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ["student-users-full"] })}
        />

        <Tabs defaultValue="all-users">
          <TabsList className={`w-full sm:w-auto grid ${isSuperadmin ? "grid-cols-5" : "grid-cols-4"} sm:inline-flex`}>
            <TabsTrigger value="all-users" className="text-xs sm:text-sm">All Users</TabsTrigger>
            <TabsTrigger value="visibility" className="text-xs sm:text-sm">Visibility</TabsTrigger>
            {isSuperadmin && (
              <TabsTrigger value="cr-roles" className="text-xs sm:text-sm">CR / Group Leader Roles</TabsTrigger>
            )}
            <TabsTrigger value="students" className="text-xs sm:text-sm">Students ({studentUsers.length})</TabsTrigger>
            <TabsTrigger value="invitations" className="text-xs sm:text-sm">Invites ({invitations.length})</TabsTrigger>
          </TabsList>

          {/* All Users Tab */}
          <TabsContent value="all-users" className="space-y-4">
            <AllUsersTab />
          </TabsContent>

          {/* Module Visibility Tab */}
          <TabsContent value="visibility" className="space-y-4">
            <ModuleVisibilityMatrix />
          </TabsContent>

          {/* CR Roles Tab (superadmin only) */}
          {isSuperadmin && (
            <TabsContent value="cr-roles" className="space-y-4">
              <AssignCrRoleTab />
            </TabsContent>
          )}
          {/* Student Users Tab */}
          <TabsContent value="students" className="space-y-4">
            <StudentSimulationCard />
            <SignupHealthCard />
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, number, or class..."
                className="pl-9"
                value={studentSearch}
                onChange={e => { setStudentSearch(e.target.value); setStudentPage(1); }}
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Student Users</CardTitle>
                <CardDescription>{filteredStudents.length} students{studentSearch && ` matching "${studentSearch}"`}</CardDescription>
              </CardHeader>
              <CardContent className="table-responsive">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">S/N</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Student No.</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden md:table-cell">Joined</TableHead>
                      <TableHead className="w-10">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedStudents.map((u, idx) => (
                      <TableRow key={u.id}>
                        <TableCell className="text-muted-foreground text-xs">{(currentPage - 1) * STUDENTS_PER_PAGE + idx + 1}</TableCell>
                        <TableCell className="font-medium text-sm">{u.full_name || "Unnamed"}</TableCell>
                        <TableCell className="font-mono text-xs">{u.student_number || "—"}</TableCell>
                        <TableCell className="text-xs">
                          {u.studentRecord?.class ? (
                            <Badge variant="outline" className="text-[10px]">{u.studentRecord.class}</Badge>
                          ) : "—"}
                        </TableCell>
                        <TableCell><StatusBadge status={(u as any).status || "active"} /></TableCell>
                        <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                          {new Date(u.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setViewStudent(u)}>
                                <Eye className="mr-2 h-4 w-4" /> View Details
                              </DropdownMenuItem>
                              {u.studentRecord && (
                                <DropdownMenuItem onClick={() => setEditStudent(u)}>
                                  <Pencil className="mr-2 h-4 w-4" /> Edit
                                </DropdownMenuItem>
                              )}
                              {canChangeEmail && (
                                <DropdownMenuItem onClick={() => setEmailStudent(u)}>
                                  <Mail className="mr-2 h-4 w-4" /> Change Email
                                </DropdownMenuItem>
                              )}
                              {isSuperadmin && (
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setDeleteStudent(u)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredStudents.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">{loadingStudents ? "Loading..." : "No students found"}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>

                {totalStudentPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-4 pt-4 border-t">
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Showing {(currentPage - 1) * STUDENTS_PER_PAGE + 1}–{Math.min(currentPage * STUDENTS_PER_PAGE, filteredStudents.length)} of {filteredStudents.length}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setStudentPage(p => p - 1)}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-xs sm:text-sm text-muted-foreground">Page {currentPage}/{totalStudentPages}</span>
                      <Button variant="outline" size="sm" disabled={currentPage >= totalStudentPages} onClick={() => setStudentPage(p => p + 1)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invitations Tab */}
          <TabsContent value="invitations">
            <Card>
              <CardHeader>
                <CardTitle>Staff Invitations</CardTitle>
                <CardDescription>Pre-registered staff accounts pending activation</CardDescription>
              </CardHeader>
              <CardContent className="table-responsive">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invitations.map((inv: any) => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-medium">{inv.full_name}</TableCell>
                        <TableCell>{inv.email}</TableCell>
                        <TableCell><Badge variant="secondary">{roleLabels[inv.role] || inv.role}</Badge></TableCell>
                        <TableCell><StatusBadge status={inv.status} /></TableCell>
                        <TableCell>{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {inv.status === "pending" && (
                            <Button variant="ghost" size="sm" onClick={() => copyInviteLink(inv.token)}>
                              <Copy className="mr-1 h-3 w-3" />Copy Link
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {invitations.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{loadingInvitations ? "Loading..." : "No invitations yet"}</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>
    </AppLayout>
  );
};

export default UserManagement;
